/**
 * SmsService
 * Service for SMS communication operations
 * Route: api/sms
 */
import { BaseService } from '../base/BaseService';
/**
 * SmsService
 * Handles SMS sending and management operations
 */
export class SmsService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? SmsService.DEFAULT_SERVICE_PATH,
            basePath: SmsService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== READ OPERATIONS ====================
    /**
     * Get all SMS messages with caching
     * GET api/sms
     */
    async getAll() {
        const response = await this.getAllRaw();
        return response.data ?? [];
    }
    /**
     * Get all SMS with full response
     */
    getAllRaw() {
        const url = this.buildUrl();
        return this.http.get(url);
    }
    /**
     * Get SMS by ID
     * GET api/sms/{id}
     */
    async getById(id) {
        const response = await this.getByIdRaw(id);
        if (!response.data) {
            throw new Error(`SMS ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get SMS by ID with full response
     */
    getByIdRaw(id) {
        const url = this.buildUrl(String(id));
        return this.http.get(url);
    }
    /**
     * Get unsent SMS messages
     * GET api/sms/unsent
     */
    async getUnsent() {
        const response = await this.getUnsentRaw();
        return response.data ?? [];
    }
    /**
     * Get unsent SMS with full response
     */
    getUnsentRaw() {
        const endpoint = this.getEndpoint('unsent', 'unsent');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== WRITE OPERATIONS ====================
    /**
     * Create a new SMS
     * POST api/sms
     */
    async create(sms) {
        const response = await this.createRaw(sms);
        if (!response.data) {
            throw new Error(response.message || 'Failed to create SMS');
        }
        return response.data;
    }
    /**
     * Create SMS with full response
     */
    createRaw(sms) {
        const url = this.buildUrl();
        return this.http.post(url, sms);
    }
    /**
     * Update an SMS
     * PUT api/sms/{id}
     */
    async update(id, sms) {
        const response = await this.updateRaw(id, sms);
        if (!response.data) {
            throw new Error(response.message || 'Failed to update SMS');
        }
        return response.data;
    }
    /**
     * Update SMS with full response
     */
    updateRaw(id, sms) {
        const url = this.buildUrl(String(id));
        return this.http.put(url, sms);
    }
    /**
     * Delete an SMS
     * DELETE api/sms/{id}
     */
    async delete(id) {
        const response = await this.deleteRaw(id);
        return response.success;
    }
    /**
     * Delete SMS with full response
     */
    deleteRaw(id) {
        const url = this.buildUrl(String(id));
        return this.http.delete(url);
    }
    // ==================== VIEW OPERATIONS ====================
    /**
     * Get all SMS messages as model views
     * GET api/sms/view
     */
    async getViews() {
        const response = await this.getViewsRaw();
        return response.data ?? [];
    }
    /**
     * Get SMS views with full response
     */
    getViewsRaw() {
        const endpoint = this.getEndpoint('view', 'view');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get SMS model view by ID
     * GET api/sms/view/{id}
     */
    async getViewById(id) {
        const response = await this.getViewByIdRaw(id);
        if (!response.data) {
            throw new Error(`SMS ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get SMS view by ID with full response
     */
    getViewByIdRaw(id) {
        const endpoint = this.getEndpoint('view-by-id', `view/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== QUEUE OPERATIONS ====================
    /**
     * Mark SMS as sent
     * POST api/sms/{id}/mark-sent
     */
    async markSent(id) {
        const response = await this.markSentRaw(id);
        return response.success;
    }
    /**
     * Mark sent with full response
     */
    markSentRaw(id) {
        const endpoint = this.getEndpoint('mark-sent', `${id}/mark-sent`);
        const url = this.buildUrl(endpoint);
        return this.http.post(url, {});
    }
    // ==================== CACHE OPERATIONS ====================
    /**
     * Invalidate SMS cache
     * POST api/sms/cache/invalidate
     */
    async invalidateCache() {
        const response = await this.invalidateCacheRaw();
        return response.success;
    }
    /**
     * Invalidate cache with full response
     */
    invalidateCacheRaw() {
        const endpoint = this.getEndpoint('cache-invalidate', 'cache/invalidate');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, {});
    }
}
SmsService.DEFAULT_SERVICE_PATH = 'communications';
SmsService.BASE_PATH = 'api/sms';
//# sourceMappingURL=SmsService.js.map